import { toast } from "sonner";
import { getTestMode } from "@/lib/testMode";

// OT authorship integrity boundary. Substantive student-authored OT fields must never accept
// precomposed/external content. The browser `paste` event fires for Cmd/Ctrl+V AND the
// browser/context-menu "Paste", so one onPaste handler covers every paste path; onDrop cancels
// dropped external text. Copy, text selection, typing, and OT NODE drag/drop are untouched (node
// DND lives on separate node containers, not on these text inputs). No AI detection, no LLM call.
//
// THE STUDENT'S OWN WORDS MAY MOVE: text the student copied or cut from their OWN writing in this
// session (remembered at copy / cut time, inside the workspace) may be pasted — cut a passage from
// the body, paste it into the conclusion. External text is still blocked. Authorship intact.
export const OT_PASTE_MSG =
  "Type your thinking here. The Writing Studio uses your work in Organizing Thinking to build a record of how your ideas develop.";

const OWN_KEPT = 6;
const OWN_KEY = "ot_own_text";   // mirrored to sessionStorage so a reload between a cut and its paste does not block the student's own words
const loadOwn = () => { try { const v = JSON.parse(sessionStorage.getItem(OWN_KEY) || "[]"); return Array.isArray(v) ? v : []; } catch { return []; } };
const persistOwn = () => { try { sessionStorage.setItem(OWN_KEY, JSON.stringify(own)); } catch { /* ignore */ } };
let own = typeof sessionStorage !== "undefined" ? loadOwn() : [];   // the student's last few copied / cut selections (normalized)
const norm = (s) => (s || "").replace(/\s+/g, " ").trim();

const selectedText = (e) => {
  const t = e && e.target;
  if (t && typeof t.selectionStart === "number" && typeof t.value === "string") return t.value.slice(t.selectionStart, t.selectionEnd);
  try { return String(document.getSelection ? document.getSelection() : ""); } catch { return ""; }
};

export const rememberOwnText = (text) => {
  const n = norm(text);
  if (!n) return;
  own = [n, ...own.filter((o) => o !== n)].slice(0, OWN_KEPT);
  persistOwn();
};
export const isOwnText = (text) => {
  const n = norm(text);
  return !!n && own.some((o) => o.includes(n));
};
export const forgetOwnText = () => { own = []; persistOwn(); };

// Spread onto the workspace containers (essay / structure): a copy or cut anywhere inside remembers the student's own selection.
export const otOwnCopy = { onCopy: (e) => rememberOwnText(selectedText(e)), onCut: (e) => rememberOwnText(selectedText(e)) };

const block = (e) => {
  if (getTestMode()) return;   // TEST MODE: allow paste/drop for manual QA
  const cd = e && e.clipboardData;
  const pasted = cd && typeof cd.getData === "function" ? cd.getData("text/plain") : "";
  if (pasted && isOwnText(pasted)) return;   // their own words, moved between parts of their essay
  e.preventDefault();
  e.stopPropagation();
  toast(OT_PASTE_MSG);
};

// Spread onto any substantive OT authoring <input>/<textarea>.
export const otNoPaste = { onPaste: block, onDrop: block };
